import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Clock, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Shows a small "N days left" pill for either the Stripe trial or the beta window.
// Clicking it takes the user to Billing to upgrade / manage.
export default function TrialCountdownBadge() {
  const { user } = useAuth();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const info = useMemo(() => {
    if (!user) return null;
    const ends = user.beta_ends_at || user.trial_ends_at;
    if (!ends) return null;
    const endMs = Date.parse(ends);
    if (isNaN(endMs)) return null;
    const diff = endMs - now;
    const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
    if (days < 0 || days > 90) return null;
    const label = user.beta_ends_at
      ? days <= 0 ? "Beta ended" : `${days} beta day${days === 1 ? "" : "s"} left`
      : days <= 0 ? "Trial ended" : `${days} trial day${days === 1 ? "" : "s"} left`;
    const tone = days <= 3
      ? "bg-rose-50 text-rose-700 border-rose-200"
      : days <= 7
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
    return { label, tone, isBeta: !!user.beta_ends_at };
  }, [user, now]);

  if (!info) return null;
  return (
    <NavLink
      to="/billing"
      data-testid="trial-countdown-badge"
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${info.tone}`}
      title={info.isBeta ? "Beta access — click to add a subscription" : "Trial ending — manage subscription"}
    >
      {info.isBeta ? <Sparkles className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
      <span>{info.label}</span>
    </NavLink>
  );
}
